import { NavigationProp } from '@react-navigation/native';
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  FlatList,
  ActivityIndicator,
  Switch,
} from 'react-native';

import backIcon from '../../assets/icons/back.png';
import { API_URL } from '../../config';
import { getAuthToken } from '../../services/auth/tokenStorage';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';
const logger = createLogger('Notifications');

interface Notification {
  id: string;
  type: string;
  category: string | null;
  title: string;
  message: string;
  actionType: string | null;
  actionData: any;
  priority: string;
  isRead: boolean;
  createdAt: string;
}

interface NotificationsScreenProps {
  navigation: NavigationProp<any>;
}

const NotificationsScreen: React.FC<NotificationsScreenProps> = ({ navigation }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // null until loaded; the toggle stores the POSITIVE sense (reminders on).
  const [remindersOn, setRemindersOn] = useState<boolean | null>(null);
  const [savingPref, setSavingPref] = useState(false);

  useEffect(() => {
    loadNotifications();
    loadEmailPreference();
  }, []);

  const loadEmailPreference = async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;
      const response = await fetch(`${API_URL}/api/email/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRemindersOn(!(data?.data?.marketingOptOut ?? false));
      }
    } catch (error) {
      logger.error('Failed to load email preferences:', error);
    }
  };

  const toggleReminders = async (next: boolean) => {
    if (savingPref) return;
    const prev = remindersOn;
    setRemindersOn(next); // optimistic
    setSavingPref(true);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('not authenticated');
      const response = await fetch(`${API_URL}/api/email/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ marketingOptOut: !next }),
      });
      if (!response.ok) throw new Error(`status ${response.status}`);
    } catch (error) {
      logger.error('Failed to save email preference:', error);
      setRemindersOn(prev); // roll back
    } finally {
      setSavingPref(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      logger.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadNotifications();
  };

  const markAsRead = async (id: string) => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      await fetch(`${API_URL}/notifications/${id}/mark-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, isRead: true } : n))
      );
    } catch (error) {
      logger.error('Failed to mark notification as read:', error);
    }
  };

  const handleNotificationPress = async (notification: Notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    if (notification.actionType === 'navigate' && notification.actionData?.screen) {
      const { screen, navigator, params } = notification.actionData;

      // If a navigator is specified, use nested navigation
      if (navigator) {
        navigation.navigate(navigator as never, { screen, params } as never);
      } else {
        // Try to determine the navigator based on screen name
        const screenToNavigatorMap: Record<string, string> = {
          // Personal screens
          'PersonalInfoInput': 'Personal',
          'PersonalInfoInputScreen': 'Personal',
          'PersonalInfoIntro': 'Personal',
          'PersonalInfoLockUnlock': 'Personal',
          'SpouseInfoIntro': 'Personal',
          'SpouseInfoInput': 'Personal',
          'ChildrenInfoIntro': 'Personal',
          'ChildrenInfoInput': 'Personal',
          'PetIntro': 'Personal',
          'PetInfoInput': 'Personal',
          'EmploymentIntro': 'Personal',
          'EmploymentInfoInput': 'Personal',
          // Medical screens
          'PrimaryPhysicianIntro': 'Medical',
          'PrimaryPhysicianInput': 'Medical',
          'SpecialtyPhysicianIntro': 'Medical',
          'MedicationsIntro': 'Medical',
          'MedicationsLockUnlock': 'Medical',
          'TreatmentsIntro': 'Medical',
          'MedicalHistoryIntro': 'Medical',
          'OrganDonorIntro': 'Medical',
          // Profile screens
          'Settings': 'Profile',
          'EditProfile': 'Profile',
          'ChangePassword': 'Profile',
          'AddSuccessor': 'Profile',
          'SuccessorDetails': 'Profile',
          'AddKeyholder': 'Profile',
          'KeyHolderDetails': 'Profile',
          'SecretCodeRecipients': 'Profile',
          'SecretCode': 'Profile',
          'DeathAccessPolicy': 'Profile',
        };

        // Normalize screen name (remove "Screen" suffix if present)
        const normalizedScreen = screen.replace(/Screen$/, '');
        const targetNavigator = screenToNavigatorMap[screen] || screenToNavigatorMap[normalizedScreen];
        if (targetNavigator) {
          // Use normalized screen name for navigation
          const screenName = normalizedScreen || screen;
          navigation.navigate(targetNavigator as never, { screen: screenName, params } as never);
        } else {
          // Fallback: try direct navigation (for screens in current navigator)
          navigation.navigate(screen as never, params as never);
        }
      }
    }
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return past.toLocaleDateString();
  };

  const renderNotification = ({ item }: { item: Notification }) => (
    <TouchableOpacity
      style={[styles.notificationCard, !item.isRead && styles.unreadCard]}
      onPress={() => handleNotificationPress(item)}
    >
      <View style={styles.notificationHeader}>
        <View style={styles.notificationTitleContainer}>
          <Text style={[styles.notificationTitle, !item.isRead && styles.unreadTitle]}>
            {item.title}
          </Text>
          {!item.isRead && <View style={styles.unreadDot} />}
        </View>
        <Text style={styles.notificationTime}>{getTimeAgo(item.createdAt)}</Text>
      </View>
      <Text style={styles.notificationMessage}>{item.message}</Text>
      {item.category && (
        <View style={styles.categoryBadge}>
          <Text style={styles.categoryText}>{item.category}</Text>
        </View>
      )}
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image source={backIcon} style={styles.headerIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Notifications</Text>
        <View style={{ width: 24 }} />
      </View>

      {remindersOn !== null && (
        <View style={styles.prefCard}>
          <View style={styles.prefText}>
            <Text style={styles.prefTitle}>Reminders & suggestions</Text>
            <Text style={styles.prefSubtitle}>
              Occasional emails and nudges about your letters and account.
              Security and account messages always arrive.
            </Text>
          </View>
          <Switch
            value={remindersOn}
            onValueChange={toggleReminders}
            disabled={savingPref}
            trackColor={{ false: '#D8DBDD', true: '#29BFC0' }}
            thumbColor="#FFFFFF"
            accessibilityLabel="Reminders and suggestions"
          />
        </View>
      )}

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#29BFC0" />
        </View>
      ) : notifications.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>No notifications yet</Text>
          <Text style={styles.emptySubtext}>
            You'll receive updates about your tasks, milestones, and important events here.
          </Text>
        </View>
      ) : (
        <FlatList
          data={notifications}
          renderItem={renderNotification}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.listContainer}
          refreshing={refreshing}
          onRefresh={handleRefresh}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(15),
    marginTop: ms(10),
    backgroundColor: '#F3F6F8',
  },
  headerIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  prefCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    marginHorizontal: ms(15),
    marginBottom: ms(10),
    padding: ms(14),
  },
  prefText: {
    flex: 1,
    marginRight: ms(12),
  },
  prefTitle: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#333',
  },
  prefSubtitle: {
    fontSize: ms(12, 0.3),
    lineHeight: ms(17, 0.3),
    color: '#666',
    marginTop: ms(2),
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(10),
  },
  emptySubtext: {
    fontSize: ms(14, 0.3),
    color: '#666',
    textAlign: 'center',
    lineHeight: ms(20, 0.3),
  },
  listContainer: {
    padding: ms(15),
  },
  notificationCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(12),
    padding: ms(15),
    marginBottom: ms(12),
    borderWidth: ms(1),
    borderColor: '#E5E7EB',
  },
  unreadCard: {
    backgroundColor: '#F0FDFF',
    borderColor: '#29BFC0',
  },
  notificationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(8),
  },
  notificationTitleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  notificationTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#333',
    flex: 1,
  },
  unreadTitle: {
    fontWeight: 'bold',
  },
  unreadDot: {
    width: s(8),
    height: s(8),
    borderRadius: ms(4),
    backgroundColor: '#29BFC0',
    marginLeft: ms(8),
  },
  notificationTime: {
    fontSize: ms(12, 0.3),
    color: '#999',
    marginLeft: ms(10),
  },
  notificationMessage: {
    fontSize: ms(14, 0.3),
    color: '#666',
    lineHeight: ms(20, 0.3),
    marginBottom: ms(8),
  },
  categoryBadge: {
    backgroundColor: '#E5F9FA',
    borderRadius: ms(12),
    paddingHorizontal: ms(10),
    paddingVertical: ms(4),
    alignSelf: 'flex-start',
  },
  categoryText: {
    fontSize: ms(12, 0.3),
    color: '#29BFC0',
    fontWeight: '500',
  },
});

export default NotificationsScreen; 